/**
 * Recherche prédictive — module « recherches récentes ».
 *
 * Responsabilité unique : le `localStorage`. Lecture et écriture des termes récents,
 * plafond, expiration, effacement unitaire et global, et rendu du bandeau par clonage
 * d'un `<template>` rendu en Liquid — jamais par construction de chaînes HTML.
 *
 * S'enregistre sous `window.MintPredictiveSearch.recent`, chargé en `<script defer>`
 * AVANT `predictive-search.js`.
 *
 * Deux contraintes de forme que les tâches suivantes ne doivent pas relâcher :
 *   - tout accès au stockage passe par un `try/catch` : `localStorage` LÈVE — il ne rend pas
 *     seulement `null` — en navigation privée, stockage désactivé ou quota plein, et le
 *     panneau doit continuer de fonctionner sans ses récentes (`state.available`) ;
 *   - le bandeau est rendu côté serveur puis peuplé par ce JS `defer`, ce qui est le
 *     profil exact du CLS de l'anti-pattern n°12. Il est sans effet à une condition :
 *     que son conteneur ne soit jamais visible au premier paint. Il naît donc dans un
 *     panneau fermé, dont le garde `[hidden]` est explicite dans la feuille (T28).
 *
 * ── Contrat de markup, à respecter par T30 / T31 / T32 ──────────────────────────────
 * Dans `[data-predictive-recent]`, tout est rendu en Liquid, libellés compris (T34) : le
 * titre (`general.search.recent`), `[data-predictive-recent-clear-all]` (`recent_clear_all`),
 * `[data-predictive-recent-list]` — VIDE au rendu serveur, peuplée ici — et
 * `<template data-predictive-recent-item>`, dont l'UNIQUE élément racine est la ligne
 * répétée : `[data-predictive-recent-select]` (le terme cliquable, éventuellement
 * `[data-predictive-recent-label]` à l'intérieur pour recevoir le texte) et
 * `[data-predictive-recent-remove]` (la croix, `recent_clear`), qui peut porter un
 * `data-label-template` contenant le jeton `__TERM__` — son `aria-label` est alors
 * personnalisé, sinon les 5 croix sont indiscernables au lecteur d'écran.
 * `[data-predictive-recent][hidden]` exige son garde CSS explicite : le `[hidden]` du
 * navigateur perd toujours contre une feuille auteur (anti-patterns n°8 et n°20).
 *
 * ── Trois instances, une seule clé ──────────────────────────────────────────────────
 * La page porte jusqu'à 3 panneaux (header desktop, header mobile, drawer) qui partagent
 * `mint-recent-searches`. Aucune ne garde d'état propre : le stockage est relu à CHAQUE
 * rendu, et toute écriture re-rend les 3 (`broadcast`). Ne pas remplacer ce `broadcast` par
 * un écouteur `storage` : il ne se déclenche QUE dans les autres onglets, jamais dans celui
 * qui écrit. L'hôte appelle `render()` à chaque ouverture de panneau — c'est ce qui rattrape
 * une écriture venue d'un autre onglet.
 *
 * ── Ce que ce module ne fait pas ────────────────────────────────────────────────────
 * Il ne remplit pas le champ, ne soumet pas le formulaire et ne pousse aucun event
 * dataLayer. Un clic sur une ligne émet `predictive-search:recent-select` (annulable) sur le
 * conteneur : l'hôte (T21/T22) écoute, remplit et annule pour empêcher une navigation. Le
 * tracking reste à T37, qui lit `data-suggestion-type` et `data-suggestion-label`, posés ici.
 *
 * Chargé en asset séparé, jamais en bloc `javascript` de section : raison écrite en
 * tête de `predictive-search.js`.
 */
(function (window, document) {
  'use strict';

  var registry = (window.MintPredictiveSearch = window.MintPredictiveSearch || {});

  /** Clé partagée par les 3 instances ; TTL filtré à la LECTURE, jamais par une purge. */
  var STORAGE_KEY = 'mint-recent-searches';
  var MAX_ENTRIES = 5;
  var TTL_MS = 30 * 24 * 60 * 60 * 1000;
  /** Longueur retenue d'un terme : borne le volume stocké sans tronquer une vraie requête. */
  var MAX_TERM_LENGTH = 120;
  var TERM_TOKEN = '__TERM__';
  /** Instances vivantes de la page, re-rendues ensemble après toute écriture. */
  var instances = new Set();

  /**
   * Le stockage est-il utilisable ? En navigation privée, `localStorage` existe mais
   * lève à l'écriture : la seule réponse fiable est d'essayer.
   */
  function isStorageAvailable() {
    try {
      window.localStorage.setItem(STORAGE_KEY + ':probe', '1');
      window.localStorage.removeItem(STORAGE_KEY + ':probe');
      return true;
    } catch (error) {
      return false;
    }
  }

  function readRaw() {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch (error) {
      return null;
    }
  }

  function writeRaw(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  function dropRaw() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      return true;
    } catch (error) {
      return false;
    }
  }

  /** Espaces réduits, bornes coupées, longueur plafonnée. Rend `''` pour tout non-terme. */
  function normalize(term) {
    if (typeof term !== 'string') return '';
    return term.replace(/\s+/g, ' ').trim().slice(0, MAX_TERM_LENGTH);
  }

  /**
   * Source de vérité. L'expiration se filtre ICI, à la lecture : une entrée de 31 jours ne
   * doit pas reparaître au prétexte qu'aucune écriture n'a eu lieu depuis. Le contenu stocké
   * vient du visiteur et peut être corrompu à la main — chaque entrée est revalidée, et un
   * JSON illisible rend une liste vide plutôt qu'une exception (anti-pattern n°22).
   */
  function readEntries() {
    var raw = readRaw();
    if (!raw) return [];
    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      return [];
    }
    if (!Array.isArray(parsed)) return [];
    var now = Date.now();
    var seen = Object.create(null);
    var entries = [];
    parsed.forEach(function (entry) {
      if (!entry || typeof entry !== 'object') return;
      var q = normalize(entry.q);
      var fresh = typeof entry.ts === 'number' && isFinite(entry.ts) && now - entry.ts <= TTL_MS;
      if (!q || !fresh || seen[q.toLowerCase()]) return;
      seen[q.toLowerCase()] = true;
      entries.push({ q: q, ts: entry.ts });
    });
    entries.sort(function (a, b) {
      return b.ts - a.ts;
    });
    return entries.slice(0, MAX_ENTRIES);
  }

  /** Écrit la liste plafonnée. Une liste vide efface la clé plutôt que d'y laisser `[]`. */
  function writeEntries(entries) {
    if (!entries.length) return dropRaw();
    return writeRaw(JSON.stringify(entries.slice(0, MAX_ENTRIES)));
  }

  function withoutTerm(entries, term) {
    var key = normalize(term).toLowerCase();
    return entries.filter(function (entry) {
      return entry.q.toLowerCase() !== key;
    });
  }

  function addTerm(term) {
    var q = normalize(term);
    if (!q) return false;
    var entries = withoutTerm(readEntries(), q);
    entries.unshift({ q: q, ts: Date.now() });
    return writeEntries(entries);
  }

  function removeTerm(term) {
    if (!normalize(term)) return false;
    return writeEntries(withoutTerm(readEntries(), term));
  }

  /**
   * Re-rend les 3 panneaux — DIFFÉRÉ D'UN TICK, et c'est une correction, pas un détail.
   *
   * Mesuré le 2026-09-16 : un clic sur une ligne récente déclenche
   * `emit('predictive-search:recent-select')`, l'hôte y répond par `recent.add(terme)`,
   * qui appelait `broadcast()` SYNCHRONEMENT — donc `render()`, donc
   * `list.textContent = ''` — pendant que l'event de clic était encore en train de
   * remonter. Le nœud cliqué était déjà détaché du document quand le clic atteignait
   * `document`, avec deux conséquences vérifiées :
   *   - `search_suggestion_click` ne partait JAMAIS sur une recherche récente, le garde
   *     `host.contains(target)` de `predictive-search-tracking.js` échouant sur un nœud
   *     détaché — alors que `data-suggestion-type="recent"` est bien posé ici ;
   *   - le `cta_location` du `click_cta` générique devenait instable (`page_body` au lieu
   *     de `header_panel`), pour la même raison.
   * L'ÉCRITURE dans le stockage, elle, reste synchrone : c'est elle qui doit être acquise
   * avant une navigation, pas le rendu. Le report ne concerne que le repeuplement du DOM.
   */
  function broadcast() {
    window.setTimeout(function () {
      instances.forEach(function (instance) {
        instance.render();
      });
    }, 0);
  }

  /**
   * @param {{ container: HTMLElement, template: HTMLTemplateElement }} options
   * @returns {object|null} `null` si le bandeau n'est pas rendu à cet emplacement.
   */
  function createRecent(options) {
    var config = options || {};
    if (!config.container || !config.template) return null;
    var container = config.container;
    var template = config.template;
    var state = { available: isStorageAvailable(), listeners: [], entries: [] };

    function listen(target, type, handler) {
      target.addEventListener(type, handler);
      state.listeners.push({ target: target, type: type, handler: handler });
    }

    function emit(name, detail, cancelable) {
      var event = new CustomEvent(name, { bubbles: true, cancelable: !!cancelable, detail: detail });
      return container.dispatchEvent(event);
    }

    function fillRow(row, entry) {
      row.setAttribute('data-recent-term', entry.q);
      var select = row.querySelector('[data-predictive-recent-select]');
      if (select) {
        (select.querySelector('[data-predictive-recent-label]') || select).textContent = entry.q;
        select.setAttribute('data-suggestion-label', entry.q);
        if (!select.hasAttribute('data-suggestion-type')) select.setAttribute('data-suggestion-type', 'recent');
      }
      var remove = row.querySelector('[data-predictive-recent-remove]');
      var pattern = remove && remove.getAttribute('data-label-template');
      if (pattern && pattern.indexOf(TERM_TOKEN) !== -1) {
        remove.setAttribute('aria-label', pattern.split(TERM_TOKEN).join(entry.q));
      }
    }

    /**
     * Relit le stockage et reconstruit la liste. Le bandeau se masque quand elle est vide —
     * un titre et un « tout effacer » sans ligne n'ont rien à effacer.
     * Le `<template>` porte UNE ligne : `firstElementChild` ignore l'indentation.
     */
    function render() {
      state.entries = state.available ? readEntries() : [];
      var list = container.querySelector('[data-predictive-recent-list]');
      if (list) {
        var fragment = document.createDocumentFragment();
        state.entries.forEach(function (entry) {
          var row = template.content && template.content.cloneNode(true).firstElementChild;
          if (!row) return;
          fillRow(row, entry);
          fragment.appendChild(row);
        });
        list.textContent = '';
        list.appendChild(fragment);
      }
      container.hidden = !list || state.entries.length === 0;
      return state.entries;
    }

    /** Délégation : la liste est reconstruite à chaque rendu, aucun écouteur par ligne. */
    function onClick(event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var control = target.closest(
        '[data-predictive-recent-clear-all],[data-predictive-recent-remove],[data-predictive-recent-select]',
      );
      if (!control || !container.contains(control)) return;

      if (control.hasAttribute('data-predictive-recent-clear-all')) {
        event.preventDefault();
        dropRaw();
        broadcast();
        emit('predictive-search:recent-cleared', {});
        return;
      }

      var row = control.closest('[data-recent-term]');
      var term = row ? row.getAttribute('data-recent-term') : '';
      if (!term) return;

      if (control.hasAttribute('data-predictive-recent-remove')) {
        event.preventDefault();
        removeTerm(term);
        broadcast();
        emit('predictive-search:recent-removed', { term: term });
        return;
      }
      if (!emit('predictive-search:recent-select', { term: term }, true)) event.preventDefault();
    }

    var api = {
      container: container,
      template: template,
      storageKey: STORAGE_KEY,
      maxEntries: MAX_ENTRIES,
      ttlMs: TTL_MS,
      state: state,
      listen: listen,
      render: render,
      /** Appelé par l'hôte quand une recherche aboutit. Re-rend les 3 panneaux de la page. */
      add: function (term) {
        var written = state.available && addTerm(term);
        broadcast();
        return written;
      },
      destroy: function () {
        state.listeners.forEach(function (entry) {
          entry.target.removeEventListener(entry.type, entry.handler);
        });
        state.listeners = [];
        instances.delete(api);
      },
    };

    listen(container, 'click', onClick);
    instances.add(api);
    render();
    return api;
  }

  registry.recent = { create: createRecent };
})(window, document);
